import React, { useEffect, useState } from "react";
import { usePartSearch } from "../hooks/usePartSearch";
import type { PartSearchHit, PartSearchHttpClient } from "../types";
import { DEFAULT_PART_SEARCH_URL } from "../utils/partSearchApi";
import { cn } from "./cn";
import { LoaderIcon } from "./icons";
import { PartSearchResultItem } from "./PartSearchResultItem";
import { ScrollingSearchInput } from "./ScrollingSearchInput";

export type PartsSearchProps = {
  axios: PartSearchHttpClient;
  searchUrl?: string;
  brand?: string;
  onSelect: (part: PartSearchHit, query: string) => void;
  showBrand?: boolean;
  showMrp?: boolean;
  limit?: number;
  disabled?: boolean;
  disabledHint?: string;
  minQueryLength?: number;
  clearOnSelect?: boolean;
  className?: string;
  /** Emphasize query matches in result rows. Default true. */
  enableHighlighting?: boolean;
};

function PartsSearchSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-2", className)}>
      <div className="h-11 w-full rounded-md border border-input bg-muted/30 animate-pulse" />
    </div>
  );
}

/** Tailwind parts search — input, scroll hints, API, results list. SSR-safe (hydrates after mount). */
export function PartsSearch({
  axios,
  searchUrl = DEFAULT_PART_SEARCH_URL,
  brand,
  onSelect,
  showBrand = true,
  showMrp = true,
  limit = 15,
  disabled = false,
  disabledHint = "Pick a vehicle first — parts are filtered to its maker brand.",
  minQueryLength = 3,
  clearOnSelect = true,
  className,
  enableHighlighting = true,
}: PartsSearchProps) {
  const [mounted, setMounted] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setMounted(true);
  }, []);

  const trimmed = query.trim();
  const partSearch = usePartSearch({
    axios,
    searchUrl,
    query,
    brand,
    limit,
    minQueryLength,
    enabled: mounted && !disabled,
  });

  const handleSelect = (hit: PartSearchHit) => {
    onSelect(hit, trimmed);
    if (clearOnSelect) setQuery("");
  };

  const showResults = mounted && trimmed.length >= minQueryLength && !disabled;

  if (!mounted) {
    return <PartsSearchSkeleton className={className} />;
  }

  return (
    <div className={cn("space-y-0", className)}>
      <ScrollingSearchInput
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        hints={["number", "name"]}
        prefix="Search by part "
        inputClassName={disabled ? "opacity-60" : undefined}
      />
      {disabled && disabledHint ? (
        <p className="text-xs text-muted-foreground mt-1.5">{disabledHint}</p>
      ) : null}
      {showResults ? (
        <div className="rounded-xl border max-h-72 overflow-y-auto overscroll-contain p-2 space-y-2 bg-muted/10 mt-2">
          {partSearch.isLoading ? (
            <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
              <LoaderIcon /> Searching…
            </div>
          ) : null}
          {!partSearch.isLoading &&
            (partSearch.data?.items ?? []).map((hit) => (
              <PartSearchResultItem
                key={hit._id}
                hit={hit}
                showBrand={showBrand}
                showMrp={showMrp}
                onSelect={handleSelect}
                enableHighlighting={enableHighlighting}
                query={trimmed}
              />
            ))}
          {!partSearch.isLoading && (partSearch.data?.items?.length ?? 0) === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">No parts found.</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
